import constants from "./constants";
import { isDriverReadyForDispatch } from "../utils/index";
import { sortBy } from "lodash";

const getValidEquipmentStatuses = (validEquipmentStatusList) => {
  return `Valid status: ${
    validEquipmentStatusList.length === 0
      ? 'None'
      : validEquipmentStatusList.join(', ')
  }`;
};

const isDriverAvailableForDispatch = (driverCurrentStatus, driverStatusConfig) => {
  return (
    driverStatusConfig.isAllowedToDispatch && driverStatusConfig.status.includes(driverCurrentStatus)
  );
};

const getEquipmentStatusesWithAllowedDispatch = (equipmentStatusConfig) => {
  return equipmentStatusConfig
    .filter((equipmentStatus) => equipmentStatus.isAllowedToDispatch)
    .map((equipmentStatus) => equipmentStatus.status);
};

const getEquipmentStatusesVisibleForDispatch = (equipmentStatusConfig) => {
  return sortBy(
    equipmentStatusConfig.filter((equipmentStatus) => equipmentStatus.isVisibleOnDispatch),
    'sortOrder'
  ).map((equipmentStatus) => equipmentStatus.status);
};

const getTrailerError = ({
  trailer,
  trailersData,
  trailerStatusConfig,
  trailerIndex = 0,
  isPreDispatch = false,
}) => {
  let error = "";
  const TRAILER_VALID_STATUS = getEquipmentStatusesWithAllowedDispatch(trailerStatusConfig);
  if (!trailer?.trailerNumber) {
    error = constants.ERROR_MESSAGES.REQUIRED;
  } else if (!TRAILER_VALID_STATUS.includes(trailer?.trailerStatus)) {
    error = getValidEquipmentStatuses(TRAILER_VALID_STATUS);
  } else if (
    trailersData.filter(
      (t) => trailer.trailerId?.toLowerCase() === t.trailerId?.toLowerCase()
    ).length > 1
  ) {
    error = constants.ERROR_MESSAGES.DUPLICATE_TRAILER;
  }
  if (
    isPreDispatch &&
    trailerIndex > 0 &&
    trailer?.isStraightTruck &&
    !trailersData[0]?.isStraightTruck
  ) {
    error = constants.ERROR_MESSAGES.STRAIGHT_TRUCK_NOT_ALLOWED_AFTER_NON_ST;
  }
  return error;
};

const getDollyError = ({dolly, trailersData, dollyStatusConfig, isPreDispatch = false}) => {
  let error = "";
  const DOLLY_VALID_STATUS = getEquipmentStatusesWithAllowedDispatch(dollyStatusConfig);
  if (!dolly) {
    error = constants.ERROR_MESSAGES.REQUIRED;
  } else if (!DOLLY_VALID_STATUS.includes(dolly?.dollyStatus)) {
    error = getValidEquipmentStatuses(DOLLY_VALID_STATUS);
  } else if (!isPreDispatch && dolly.label === constants.DEFAULT_DOLLY) {
    error = constants.ERROR_MESSAGES.DEFAULT_DOLLY;
  } else if (
    trailersData.filter(
      (t) =>
      t.dolly?.value?.toLowerCase() === dolly.value?.toLowerCase() &&
      dolly.label !== constants.ADHOC_DOLLY && dolly.label !== constants.DEFAULT_DOLLY
    ).length > 1
  ) {
    error = constants.ERROR_MESSAGES.DUPLICATE_DOLLY;
  }
  return error;
};

const getTractorError = ({tractor,trailerNumber, tractorStatusConfig, isStraightTruckTrailer,AllowAnyStraightTruckInDriverDispatch, isPreDispatch = false}) => {
  let error = null;
  if (!tractor) {
    return constants.ERROR_MESSAGES.REQUIRED;
  }  
  if(isPreDispatch && isStraightTruckTrailer){
    if(AllowAnyStraightTruckInDriverDispatch && !tractor.isStraightTruck){
      return constants.ERROR_MESSAGES.STRAIGHT_TRUCK_REQUIRED;
    }
    else if(!AllowAnyStraightTruckInDriverDispatch && trailerNumber!==tractor.label){
      return constants.ERROR_MESSAGES.SAME_STRAIGHT_TRUCK_REQUIRED;
    }
  }
  // no validations required if straight truck
  if (tractor && tractor.isStraightTruck){
    return error
  } 
  // if straight truck is selected, tractor should be straight truck
  if (isStraightTruckTrailer && !tractor.isStraightTruck) {
    return constants.ERROR_MESSAGES.STRAIGHT_TRUCK_REQUIRED;
  }
  const TRACTOR_VALID_STATUS = getEquipmentStatusesWithAllowedDispatch(tractorStatusConfig);
  if (!TRACTOR_VALID_STATUS.includes(tractor.tractorStatus)) {
    error = getValidEquipmentStatuses(TRACTOR_VALID_STATUS);
  }
  if (!isPreDispatch && tractor.label === constants.DEFAULT_TRACTOR) {
    error = constants.ERROR_MESSAGES.DEFAULT_TRACTOR;
  }
  return error;
};

const getDriverError = ({
  driverData,
  driverStatusConfig,
  terminalOffset,
  isDriverNotYetDispatched,
  isPreDispatch = false,
  isPreSelected = false,
  driverDispatchRouteCount = 0,
  maxAllowedTrailerCount = 0,
  isDriverDispatchedWithST = false,
  isPreSelectedTrailerIsST = false,
}) => {
  if (isPreDispatch) {
    if (maxAllowedTrailerCount > 0 && driverDispatchRouteCount >= maxAllowedTrailerCount) {
      return { error: constants.ERROR_MESSAGES.MAX_ALLOWED_TRAILERS, statusError: "" };
    }
    if (isDriverDispatchedWithST) {
      return { error: constants.ERROR_MESSAGES.ALREADY_DISPATCHED_STRAIGHT_TRUCK, statusError: "" };
    }
    if (isPreSelectedTrailerIsST && driverDispatchRouteCount > 0) {
      return { error: constants.ERROR_MESSAGES.ACTIVE_DRIVER_CANNOT_PRE_DISPATCH_STRAIGHT_TRUCK, statusError: "" };
    }
  }
  if (isPreDispatch || isPreSelected) {
    if (!driverData?.driverId) {
      return { error: constants.ERROR_MESSAGES.REQUIRED, statusError: "" };
    }
    return { error: "", statusError: "" };
  }

  let error = "";
  let statusError = "";
  if (!driverData) {
    error = constants.ERROR_MESSAGES.REQUIRED;
  } else if(!isDriverNotYetDispatched){
    error = constants.ERROR_MESSAGES.DRIVER_DISPATCHED;
  } else if (!isDriverAvailableForDispatch(driverData.as400Status, driverStatusConfig)) {
    statusError = `The current driver status is "${driverData.as400Status}"`;
    error = constants.ERROR_MESSAGES.VALID_DRIVER_STATUS;
  } else if (
    !isDriverReadyForDispatch({driverClockedInTime:driverData.clockedInTime, isCompanyDriver: driverData.isCompanyDriver, terminalOffset:terminalOffset})
  ) {
    error = constants.ERROR_MESSAGES.DRIVER_CLOCKED_IN_STATUS;
  }
  return { error, statusError };
};

const getDriverErrorArgs = ({
  driverId,
  driverData,
  driverStatusConfig,
  terminalOffset,
  isDriverNotYetDispatched,
  isPreDispatch,
  isPreSelected,
  dispatchedDrivers,
  maxAllowedTrailerCount = 0,
  driverDataFromStore = null,
  preSelectedTrailer = null,
}) => {
  const args = {
    driverData,
    driverStatusConfig,
    terminalOffset,
    isDriverNotYetDispatched,
    isPreDispatch: Boolean(isPreDispatch),
    isPreSelected,
  };
  if (isPreDispatch) {
    const driverFromStore = driverDataFromStore ?? (dispatchedDrivers?.find((d) => d.driverId === driverId) ?? null);
    const isDriverDispatchedWithST =
      driverFromStore?.dispatchRoutes?.some((route) => route.trailer?.isStraightTruck) ?? false;
    const isPreSelectedTrailerIsST = preSelectedTrailer?.length ? preSelectedTrailer[0]?.isStraightTruck : false;
    Object.assign(args, {
      driverDispatchRouteCount: driverFromStore?.dispatchRoutes?.length ?? 0,
      maxAllowedTrailerCount: maxAllowedTrailerCount ?? 0,
      isDriverDispatchedWithST: isDriverDispatchedWithST ?? false,
      isPreSelectedTrailerIsST: isPreSelectedTrailerIsST ?? false,
    });
  }
  return args;
};
const getUpdatedTrailerError = ({
  trailerDetails,
  trailerDetailsErrors,
  trailerStatusConfig,
  dollyStatusConfig,
  isPreDispatch = false,
  isSubmitPreDispatch = isPreDispatch,
  routeNameRequiredForDispatch = false,
}) => {
  return trailerDetails.map((trailer, index) => {
    let trailerError = getTrailerError({
      trailer,
      trailersData: trailerDetails,
      trailerStatusConfig,
      trailerIndex: index,
      isPreDispatch,
    });
    // Dolly belongs to rows after the first; the first row has no dolly control.
    let dollyError = index === 0
      ? ""
      : getDollyError({
          dolly: trailer.dolly,
          trailersData: trailerDetails,
          dollyStatusConfig,
          isPreDispatch: isSubmitPreDispatch,
        });
    if (
      trailerDetailsErrors[index]?.trailerNumber !== trailerError &&
      [constants.ERROR_MESSAGES.REQUIRED].includes(trailerError)
    ) {
      trailerError = trailerDetailsErrors[index]?.trailerNumber;
    }
    if (
      index !== 0 &&
      trailerDetailsErrors[index]?.dolly !== dollyError &&
      [constants.ERROR_MESSAGES.REQUIRED].includes(dollyError)
    ) {
      dollyError = trailerDetailsErrors[index]?.dolly;
    }
    const routeNameError = getRouteNameDispatchError({
      trailer,
      routeNameRequiredForDispatch,
    });
    return {
      trailerNumber: trailerError,
      dolly: dollyError,
      routeName: routeNameError,
    };
  });
};

const getRouteNameDispatchError = ({ trailer, routeNameRequiredForDispatch }) => {
  if (!routeNameRequiredForDispatch) {
    return "";
  }
  const rn = trailer?.routeName;
  if (
    rn == null ||
    String(rn).trim() === "" ||
    rn === constants.ROUTE_NAME_DEFAULT
  ) {
    return constants.ERROR_MESSAGES.REQUIRED;
  }
  return "";
};

const getEventFailureMessage = () => {
  return constants.ERROR_MESSAGES.DISPATCH_DRIVER_EVENT_FAILED;
};

/** True when a tractor is selected and is not the /TBF default. */
const hasDispatchableTractorSelected = (tractor) => {
  const label = tractor?.label;
  if (!label) {
    return false;
  }
  return label !== constants.DEFAULT_TRACTOR;
};

/**
 * Dolly is only required for trailer rows after the first.
 * True when every such row has a dolly selected that is not the /TBF default.
 */
const hasDispatchableDolliesSelected = (trailerDetails) => {
  if (!trailerDetails?.length) {
    return true;
  }
  return trailerDetails.every((trailer, index) => {
    if (index === 0) {
      return true;
    }
    const label = trailer?.dolly?.label;
    if (!label) {
      return true;
    }
    return label !== constants.DEFAULT_DOLLY;
  });
};

const hasAlreadyDispatchedRoute = (trailerDetails) =>
  Boolean(trailerDetails?.some((trailer) => trailer?.isAlreadyDispatchedRoute));

/**
 * Submit as normal Dispatch only from the Closed Trailers panel when the driver is
 * eligible (company clocked-in / non-company always), a dispatchable tractor is selected
 * (not /TBF), every required dolly is dispatchable (not /TBF), and no row is an
 * already-dispatched route. Otherwise Pre-Dispatch.
 */
const resolveDispatchSubmitMode = ({
  isPreDispatchPanel,
  isDriverEligibleForNormalDispatch,
  tractor,
  trailerDetails,
}) => {
  if (!isPreDispatchPanel) {
    return false;
  }
  if (!isDriverEligibleForNormalDispatch) {
    return true;
  }
  if (!hasDispatchableTractorSelected(tractor)) {
    return true;
  }
  if (!hasDispatchableDolliesSelected(trailerDetails)) {
    return true;
  }
  if (hasAlreadyDispatchedRoute(trailerDetails)) {
    return true;
  }
  return false;
};

const getDispatchSubmitLabel = (isPreDispatchSubmit) =>
  isPreDispatchSubmit
    ? constants.SUBMIT_LABELS.PRE_DISPATCH
    : constants.SUBMIT_LABELS.DISPATCH;

const getUniqueEquipmentNumber = (equipmentNumber, equipmentScac = null) => {
  return equipmentScac ? `${equipmentNumber}_${equipmentScac}` : equipmentNumber;
};

// function to find matching straight tractor for a trailer 
const findMatchingStraightTractor = (trailerData, tractorList) => {
    if (trailerData?.isStraightTruck) {
      const uniqueTrailerNumber = getUniqueEquipmentNumber(trailerData.trailerNumber, trailerData?.trailerScac);
      const straightTractor = tractorList.find(
        (tractor) => {
          const uniqueTractorNumber = getUniqueEquipmentNumber(tractor.label, tractor?.tractorScac);
          return uniqueTractorNumber === uniqueTrailerNumber
        }) ?? null;
      return straightTractor;
    }
    return null;
  };

export {
  getTrailerError,
  getTractorError,
  getDollyError,
  getDriverError,
  isDriverAvailableForDispatch,
  getDriverErrorArgs,
  getUpdatedTrailerError,
  getRouteNameDispatchError,
  getEquipmentStatusesVisibleForDispatch,
  getEventFailureMessage,
  getUniqueEquipmentNumber,
  getEquipmentStatusesWithAllowedDispatch,
  findMatchingStraightTractor,
  hasDispatchableTractorSelected,
  hasDispatchableDolliesSelected,
  resolveDispatchSubmitMode,
  getDispatchSubmitLabel,
};
